import {
	Button,
	Chip,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid,
	IconButton,
	InputLabel,
	makeStyles,
	MenuItem,
	Select,
	Tooltip
} from '@material-ui/core'
import CheckBoxOutlineBlankRounded from '@material-ui/icons/CheckBoxOutlineBlankRounded'
import CheckBoxRounded from '@material-ui/icons/CheckBoxRounded'
import Clear from '@material-ui/icons/Clear'
import {
	Elements,
	enums,
	hooks,
	Modal,
	TextButton,
	TextInput
} from '@tfo/common'
import classnames from 'classnames'
import * as React from 'react'
import { useIntl } from 'react-intl'
import Form from '../../../containers/client/common/form/Form'
import { LoginMethod } from '../../../models/enums/LoginMethod'
import { ICompanyUser } from '../../../models/ICompanyUser'
import $http from '../../../utils/$http'
import '../ModalBase.scss'

const { Label, Paragraph } = Elements

interface CustomerUserProps {
	data: ICompanyUser
	newUser: boolean
	onUserChanged: Function
}
const useStyle = makeStyles({
	heading: { marginTop: '0' },
	inputNewUser: { margin: '2% 5%', width: '60%', height: '20%' },
	formControl: { width: '60%', margin: '5% 0' },
	formControlRecipientChips: { width: '60%', margin: '0%' },
	formControlRecipient: { width: '60%', margin: '3% 0' },
	deleteButton: { position: 'absolute', top: '2.4rem', left: '2.4rem' },
	confirmButtons: { marginBottom: '-4rem' },
	confirmText: { marginBottom: '4rem' },
	successText: { color: 'green' },
	errorText: { color: 'red' },
	paragraphColor: { color: '#0000008a' },
	recipientChips: { margin: '0.2rem' },
	recipientLabelDisplay: { margin: '1rem 0 ', display: 'flex' },
	helperText: { margin: '1rem 20% 0 20%' }
})

export const CompanyUserModal = (props) => {
	const { onCloseClick, data, newUser, companyUsers, onUserChanged } = props
	const classes = useStyle()
	const intl = useIntl()
	const rootStore = hooks.useStore()
	const { network, clientViewStore } = rootStore
	const headingTitle: string = newUser ? 'newUserTitle' : 'editUserTitle'
	const buttonTitle: string = newUser ? 'createNewUser' : 'updateUser'
	const [responseMessage, setResponseMessage] = React.useState('')
	const [displayErrorRecipient, setDisplayErrorRecipient] = React.useState<boolean>(false)
	const [openConfirmationModal, setModal] = React.useState(Boolean)
	const [recipients, setRecipients] = React.useState<ICompanyUser[]>()

	const userTypes = [
		enums.CompanyUserRoles.SuperUser,
		enums.CompanyUserRoles.Orderer,
		enums.CompanyUserRoles.CompanyUser]

	React.useEffect(() => {
		setRecipients(() => companyUsers.filter(u => u.id !== user.id && u.roles.find(r => r.role !== enums.CompanyUserRoles.Orderer) && u.isActive))
	}, [])

	// Check if user is SSO to disable certain fields
	const isSSOUser = data?.loginType === LoginMethod.SSO

	let thisRole = { role: 'CompanyUser', referenceId: null }
	if (data?.roles) {
		thisRole = data?.roles[0] ?? { role: 'CompanyUser', referenceId: null }
	}
	const [user, setUser] = React.useState({
		id: data?.id,
		companyId: data?.companyId,
		firstName: data?.firstName,
		lastName: data?.lastName,
		email: data?.email,
		phoneNumber: data?.phoneNumber,
		role: thisRole,
		sendEmail: newUser,
		recipientIds: newUser ? [] : data?.recipientIds,
		receiverWhenOrdering: newUser ? false : data?.receiverWhenOrdering
	})

	React.useEffect(() => {
		if (user.receiverWhenOrdering) {
			setUser(prev => ({ ...prev, recipientIds: [] }))
		}
	}, [user.receiverWhenOrdering])

	const isValid = () => {
		if ((user.role.role === enums.CompanyUserRoles.Orderer && !(user.receiverWhenOrdering ?? false)) && (user.recipientIds === null || user.recipientIds === undefined || user.recipientIds.length <= 0)) {
			setDisplayErrorRecipient(() => true)
			return false
		}
		else {
			setDisplayErrorRecipient(() => false)
			return true
		}
	}

	const save = async () => {
		const companyData = clientViewStore.flatSubsidiaries.find(c => c.id === user.companyId);
		const isOrderer = user.role.role === enums.CompanyUserRoles.Orderer
		const validRecipientList = isOrderer ? user.recipientIds : []
		const _user = {
			...user,
			recipientIds: validRecipientList,
			role: { role: user.role.role, referenceId: null },
			language: companyData?.language
		}
		setUser({ ...user, role: { role: user.role.role, referenceId: null } })
		if (isValid()) {
			if (newUser) {
				await $http.post<any>('/user/customer', _user).then((response) => {
					if (response.customer) {
						onUserChanged(_user)
						setResponseMessage(intl.formatMessage({ id: 'common.saved' }))
						setTimeout(() => {
							onCloseClick()
						}, 2000)
					} else {
						setResponseMessage(intl.formatMessage({ id: response.errorMessage }))
					}
				}).catch(err => {
					setResponseMessage(intl.formatMessage({ id: 'common.notSaved' }))
					console.error(err)
				})
				setTimeout(() => {
					setResponseMessage('')
				}, 3000)
			}
			else {
				await $http.put<any>(`/user/customer/${_user.id}`, _user).then((response) => {
					onUserChanged(_user)
					setResponseMessage(intl.formatMessage({ id: 'common.saved' }))
					setTimeout(() => {
						onCloseClick()
					}, 2000)
				}).catch(err => {
					setResponseMessage(intl.formatMessage({ id: 'common.notSaved' }))
					console.error(err)
				})
				setTimeout(() => {
					setResponseMessage('')
				}, 3000)
			}
		}
	}

	const openCustomerConfirmationModal = () => {
		setModal(true)
	}

	const onSetSelectedRecipient = (event: any) => {
		const { name, value } = event.target
		setUser(() => ({
			...user,
			[name]: [value]
		}))
	}

	const closeCustomerConfirmationModal = (closeParentModal?: boolean) => {
		if (closeParentModal) {
			onUserChanged()
			setModal(false)
			onCloseClick()
		} else {
			setModal(false)
		}

	}

	return (
		<>
			<Modal.Modal
				className={classnames('CompanyUserModal')}
				onCloseClick={onCloseClick}
				large>
				{!newUser && (
					<div className={classes.deleteButton}>
							<Button 
								variant="outlined" 
								onClick={openCustomerConfirmationModal}>
								{intl.formatMessage({ id: 'confirmation.modal.delete.buttontext' })}
							</Button>
						</div>
				)}
				<Form
					noValidate
					autoComplete={'off'}
					onSubmit={(e: React.FormEvent) => {
						e.stopPropagation()
						e.preventDefault()
					}}>

					<Grid container className="CompanyUserModal" direction="column">
						<h2 className={classes.heading}>
							{intl.formatMessage({
								id: `companyUserModal.${headingTitle}`,
							})}
						</h2>
						<Grid item >
							<FormControl variant="filled" className={classes.formControl}>
								<InputLabel htmlFor="company-native">
									{intl.formatMessage({
										id: 'controlpoint.company',
									})}
								</InputLabel>
								<Select
									value={user.companyId}
									defaultValue=""
									onChange={event =>
										setUser({ ...user, companyId: event.target.value as number })
									}
									variant="outlined"
									required>
									{Object.values(clientViewStore.flatSubsidiaries).map(
										company => (
											<MenuItem key={company.id} value={company.id}>
												{company.name}
											</MenuItem>
										),
									)}
								</Select>
							</FormControl>
						</Grid>
						<Grid item style={{ marginBottom: '2%' }}>
							<FormControl variant="filled" className={classes.formControl}>
								<InputLabel htmlFor="userRole-native">
									{intl.formatMessage({
										id: 'companyUserRoles.title',
									})}
								</InputLabel>
								<Select
									defaultValue="CompanyUser"
									value={user.role.role}
									onChange={event =>
										setUser({
											...user,
											role: {
												role: event.target.value as enums.CompanyUserRoles,
												referenceId: null,
											},
										})
									}
									variant="outlined"
									fullWidth
									required>
									{userTypes.map(roleType => (
										<MenuItem key={roleType} value={roleType}>
											{intl.formatMessage({ id: `companyUserRoles.${roleType}` })}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						{user.role.role === enums.CompanyUserRoles.Orderer &&
							<Grid item>
								<FormHelperText className={classes.helperText}>{intl.formatMessage({ id: 'customer.manage.users.orderer.info' })}</FormHelperText>
								<FormControlLabel
									className={classes.formControl}
									disabled={user?.recipientIds.length > 1}
									label={intl.formatMessage({ id: 'customer.manage.users.receiver.when.ordering.label' })}
									onClick={() => {
										if (user?.recipientIds.length > 1) {
											return
										}
										setUser({ ...user, receiverWhenOrdering: user.receiverWhenOrdering ? !user.receiverWhenOrdering : true })
									}}
									control={
										<IconButton
											disabled={user?.recipientIds.length > 1}
											value={user.receiverWhenOrdering ?? false}
										>
											{user.receiverWhenOrdering
												? <CheckBoxRounded style={{ color: '#0098C3' }} fontSize="large" />
												: <CheckBoxOutlineBlankRounded style={{ opacity: '.5' }} fontSize="large" />
											}
										</IconButton>
									}
								/>
							</Grid>
						}
						{
							recipients && user.role.role === enums.CompanyUserRoles.Orderer &&
							<Grid item >
								{user.recipientIds.length > 1 ?
									<FormControl variant="filled" className={classes.formControlRecipientChips}>
										<div className={classes.recipientLabelDisplay}>
											<Paragraph className={classes.paragraphColor}>{intl.formatMessage({ id: 'companyUserRoles.reciverOfBackroundcheck' })}</Paragraph>
										</div>
										<div>
											{
												user.recipientIds.map(recipient => (
													<Chip className={classes.recipientChips} key={recipient} label={recipients.find(r => r.id === recipient).firstName + ' ' + recipients.find(r => r.id === recipient).lastName} />
												))}
										</div>
									</FormControl> :
									<>
										<FormControl variant="filled" className={classes.formControlRecipient} disabled={user.receiverWhenOrdering}>
											{user.recipientIds[0] &&
												<IconButton
													style={{ position: 'absolute', right: '20px', top: '5px', zIndex: '1' }}
													onClick={() => setUser({ ...user, recipientIds: [] })}
												>
													<Clear></Clear>
												</IconButton>
											}
											<InputLabel htmlFor="recipientIds-native">
												{intl.formatMessage({
													id: 'companyUserRoles.reciverOfBackroundcheck',
												})}
											</InputLabel>
											<Select
												error={displayErrorRecipient}
												value={user.recipientIds[0] ?? ''}
												name="recipientIds"
												onChange={event => onSetSelectedRecipient(event)}
												variant="outlined"
												fullWidth
												required>
												{recipients.length <= 0 &&
													<MenuItem>
														{intl.formatMessage({ id: 'companyUserRoles.missing.reciverOfBackroundcheck' })}
													</MenuItem>}
												{recipients.length > 0 && recipients.map(recipient => (
													<MenuItem key={recipient.id} value={recipient.id}>
														{recipient.firstName + ' ' + recipient.lastName}
													</MenuItem>
												))}
											</Select>
											{displayErrorRecipient && <FormHelperText>{intl.formatMessage({ id: 'companyUserRoles.error.reciverOfBackroundcheck' })}</FormHelperText>}
										</FormControl>
									</>
								}
							</Grid>
						}

						<Grid item xs style={{ marginTop: '2%' }}>
							<TextInput
								className={classes.inputNewUser}
								id="firstname"
								value={user?.firstName}
								placeholder={intl.formatMessage({
									id: 'neworder.firstname.placeholder',
								})}
								disabled={isSSOUser}
								onChange={event =>
									setUser({ ...user, firstName: event.target.value })
								}
							/>
						</Grid>
						<Grid item>
							<TextInput
								className={classes.inputNewUser}
								id="lastname"
								value={user?.lastName}
								placeholder={intl.formatMessage({
									id: 'neworder.lastname.placeholder',
								})}
								disabled={isSSOUser}
								onChange={event =>
									setUser({ ...user, lastName: event.target.value })
								}
							/>
						</Grid>
						<Grid item>
							<TextInput
								className={classes.inputNewUser}
								id="epost"
								value={user?.email}
								placeholder={intl.formatMessage({
									id: 'neworder.email.placeholder',
								})}
								disabled={isSSOUser}
								onChange={event =>
									setUser({ ...user, email: event.target.value })
								}
							/>
						</Grid>
						<Grid item>
							<TextInput
								className={classes.inputNewUser}
								id="phoneNumber"
								value={user?.phoneNumber}
								placeholder={intl.formatMessage({
									id: 'checkpoint.referencePerson.phoneNumer',
								})}
								disabled={isSSOUser}
								onChange={event =>
									setUser({ ...user, phoneNumber: event.target.value })
								}
							/>
						</Grid>
						<Grid item>
							<div className={'NewReportOrder__BlockSpace--medium'}>
								<Label style={{ padding: '0 3rem 0 3rem', whiteSpace: 'pre-line' }}>{responseMessage}</Label>
							</div>
						</Grid>

						<Grid item>
							<div className={'NewReportOrder__BlockSpace--medium'}>
								<TextButton
									style={{ width: '93%', margin: '0 3.5%' }}
									color="primary"
									disabled={isSSOUser}
									onClick={save}>
									{intl.formatMessage({
										id: `companyUserModal.${buttonTitle}`,
									})}
								</TextButton>
							</div>
						</Grid>
					</Grid>
				</Form>
			</Modal.Modal>
			{openConfirmationModal && <DeleteUserConfirmationModal user={data} onCloseClick={(closeParentModal) => closeCustomerConfirmationModal(closeParentModal)} />}
		</>
	)
}

const DeleteUserConfirmationModal: React.FunctionComponent<Modal.ModalProps &
{
	user: ICompanyUser
	onCloseClick: Function
}> = props => {
	const { onCloseClick, user } = props
	const styles = useStyle()
	const intl = useIntl()
	const rootStore = hooks.useStore()
	const { network } = rootStore
	const [responseMessage, setResponseMessage] = React.useState('')

	const inactivate = async () => {
		await $http.delete<any>(`/user/customer/${user.id}`)
			.then(() => {
				setResponseMessage(intl.formatMessage({ id: 'confirmation.modal.user.deleted' }))
				setTimeout(() => {
					onCloseClick(true)
				}, 2000)
			}).catch((err) => {
				setResponseMessage(intl.formatMessage({ id: 'confirmation.modal.user.not.deleted' }))
				console.error(err)
				setTimeout(() => {
					setResponseMessage('')
				}, 4000)
			})
	}
	const closeModal = () => {
		onCloseClick()
	}

	return (
		<Modal.Modal
			onCloseClick={onCloseClick}>
			<Grid item xs={12}>
				<Paragraph className={`${(responseMessage === intl.formatMessage({ id: 'confirmation.modal.user.deleted' })) ? styles.successText : styles.errorText} Paragraph--Bold`}>
					{responseMessage}
				</Paragraph>
			</Grid>

			<Grid container spacing={1} justifyContent="space-between" >
				<Grid item xs={12}
					className={styles.confirmText}
				>
					<Paragraph>{intl.formatMessage({ id: 'confirmation.modal.question' }) + ' '}
						<b>{intl.formatMessage({ id: 'confirmation.modal.question.delete' })}</b> {intl.formatMessage({ id: 'confirmation.modal.question.theuser' })}?
					</Paragraph>
				</Grid>

				<Grid container className={styles.confirmButtons}>
					<Grid item xs={6}>
						<Button
							variant="contained"
							color="secondary"
							onClick={closeModal}
						>
							{intl.formatMessage({ id: 'confirmation.modal.cancel.buttontext' })}
						</Button>
					</Grid>
					<Grid item xs={6}>
						<Button
							variant="contained"
							className="modalDeleteButton warning"
							onClick={inactivate}
						>
							{intl.formatMessage({ id: 'confirmation.modal.inactivate.buttontext' })}
						</Button>
					</Grid>
				</Grid>
			</Grid>

		</Modal.Modal>

	)
}

export default CompanyUserModal
